import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Wrapper } from "src/components";
import { theme } from "src/shared/theme/theme";
import tokens from "src/shared/theme/tokens";

export type RenewalWizardPath = "choose" | "renew" | "ntv";

type RenewalWizardShellProps = {
  path: RenewalWizardPath;
  /** 0-based index within the visible step list */
  activeStep: number;
  footer?: React.ReactNode;
  children: React.ReactNode;
  maxWidth?: number;
};

/**
 * Renewal / NTV step chrome inside AppShell (left nav / mobile tabs stay
 * visible). Stepper + scroll body + sticky footer — no second brand bar.
 */
export const RenewalWizardShell = ({
  path,
  activeStep,
  footer,
  children,
  maxWidth = 880,
}: RenewalWizardShellProps) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const stepTitles =
    path === "ntv"
      ? [
          t("renewal.steps.choose"),
          t("renewal.steps.ntvSummary"),
          t("renewal.steps.ntvDetails"),
          t("renewal.steps.ntvConfirm"),
        ]
      : [
          t("renewal.steps.choose"),
          t("renewal.steps.term"),
          t("renewal.steps.next"),
        ];

  const totalSteps = stepTitles.length;
  // activeStep >= totalSteps → all steps marked complete (terminal screens)
  const completed = activeStep >= totalSteps;
  const clamped = Math.min(Math.max(activeStep, 0), totalSteps - 1);
  const stepperActive = completed ? totalSteps : clamped;

  return (
    <Wrapper isAuthorizedWrapper idSelector="wrapperContainer">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, md: 4 },
            py: { xs: 1.5, sm: 2 },
            backgroundColor: tokens.colors.surface,
            borderBottom: `1px solid ${tokens.colors.border}`,
          }}
        >
          <Box sx={{ maxWidth, mx: "auto", width: "100%" }}>
            {isMobile ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  {t("renewal.steps.of", {
                    current: completed ? totalSteps : clamped + 1,
                    total: totalSteps,
                  })}{" "}
                  · {stepTitles[clamped]}
                </Typography>
                <Box
                  sx={{
                    mt: 1.5,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: tokens.colors.border,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${
                        ((completed ? totalSteps : clamped + 1) / totalSteps) *
                        100
                      }%`,
                      height: "100%",
                      backgroundColor: tokens.colors.primary,
                      transition: "width 0.25s ease",
                    }}
                  />
                </Box>
              </>
            ) : (
              <Stepper activeStep={stepperActive} alternativeLabel>
                {stepTitles.map((title) => (
                  <Step key={title}>
                    <StepLabel
                      sx={{
                        "& .MuiStepLabel-label": {
                          typography: "caption",
                          fontWeight: 600,
                        },
                      }}
                    >
                      {title}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Box
            sx={{
              maxWidth: `${maxWidth}px`,
              mx: "auto",
              width: "100%",
              px: { xs: 2, md: 4 },
              py: { xs: 3, md: 4 },
            }}
          >
            {children}
          </Box>
        </Box>

        {footer && (
          <Box
            sx={{
              flexShrink: 0,
              backgroundColor: tokens.colors.surface,
              borderTop: `1px solid ${tokens.colors.border}`,
            }}
          >
            <Box
              sx={{
                maxWidth: `${maxWidth}px`,
                mx: "auto",
                width: "100%",
                px: { xs: 2, md: 4 },
                py: 2,
              }}
            >
              {footer}
            </Box>
          </Box>
        )}
      </Box>
    </Wrapper>
  );
};

export const renewalWizardFooterSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 2,
  flexWrap: "wrap" as const,
};
